import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Filter, Mail, Phone, Calendar, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

// Mock data for clients
const mockClients = [
  {
    id: '1',
    name: 'Sophie Dupont',
    email: 'sophie.dupont@example.com',
    phone: '+33 6 12 34 56 78',
    company: 'Dupont Consulting',
    createdAt: '2023-01-15',
    lastOrder: '2023-06-15',
    totalOrders: 3,
    totalSpent: 1540,
  },
  {
    id: '2',
    name: 'Thomas Laurent',
    email: 'thomas.laurent@example.com',
    phone: '+33 6 23 45 67 89',
    company: 'TechSolutions SAS',
    createdAt: '2023-02-20',
    lastOrder: '2023-06-22',
    totalOrders: 1,
    totalSpent: 450,
  },
  {
    id: '3',
    name: 'Marie Fontaine',
    email: 'marie.fontaine@example.com',
    phone: '+33 6 34 56 78 90',
    company: 'Fontaine & Associés',
    createdAt: '2023-03-10',
    lastOrder: '2023-06-25',
    totalOrders: 2,
    totalSpent: 1050,
  },
  {
    id: '4',
    name: 'Jean Dubois',
    email: 'jean.dubois@example.com',
    phone: '+33 6 45 67 89 01',
    company: 'Dubois Entreprise',
    createdAt: '2023-04-05',
    lastOrder: '2023-06-10',
    totalOrders: 1,
    totalSpent: 550,
  },
  {
    id: '5',
    name: 'Lucie Martin',
    email: 'lucie.martin@example.com',
    phone: '+33 6 56 78 90 12',
    company: 'Martin Design',
    createdAt: '2023-05-12',
    lastOrder: '2023-06-08',
    totalOrders: 2,
    totalSpent: 1275,
  },
];

const ClientManagement: React.FC = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentClient, setCurrentClient] = useState<any | null>(null);
  
  // Fetch clients
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchClients = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setClients(mockClients);
        setFilteredClients(mockClients);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClients();
  }, []);
  
  // Filter clients based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(client => 
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.company.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [searchTerm, clients]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleAddClient = () => {
    setCurrentClient({
      id: '',
      name: '',
      email: '',
      phone: '',
      company: '',
      createdAt: new Date().toISOString().split('T')[0],
      lastOrder: '',
      totalOrders: 0,
      totalSpent: 0,
    });
    setIsModalOpen(true);
  };
  
  const handleEditClient = (client: any) => {
    setCurrentClient({ ...client });
    setIsModalOpen(true);
  };
  
  const handleDeleteClient = (clientId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      // In a real app, this would be an API call
      setClients(prevClients => prevClients.filter(client => client.id !== clientId));
      setFilteredClients(prevClients => prevClients.filter(client => client.id !== clientId));
      toast.success('Client supprimé avec succès');
    }
  };
  
  const handleSubmitClient = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentClient) return;
    
    // Validate form
    if (!currentClient.name || !currentClient.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // In a real app, this would be an API call
    if (currentClient.id) {
      // Update existing client
      setClients(prevClients => 
        prevClients.map(client => client.id === currentClient.id ? currentClient : client)
      );
      setFilteredClients(prevClients => 
        prevClients.map(client => client.id === currentClient.id ? currentClient : client)
      );
      toast.success('Client mis à jour avec succès');
    } else {
      // Add new client
      const newClient = {
        ...currentClient,
        id: Date.now().toString(),
      };
      setClients(prevClients => [...prevClients, newClient]);
      setFilteredClients(prevClients => [...prevClients, newClient]);
      toast.success('Client ajouté avec succès');
    }
    
    setIsModalOpen(false);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des clients</h1>
        
        <button
          onClick={handleAddClient}
          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
        >
          <Plus className="h-5 w-5 mr-2" />
          Ajouter un client
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher un client..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client depuis
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière commande
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun client trouvé
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{client.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Mail className="h-4 w-4 text-gray-500 mr-1" />
                        {client.email}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Phone className="h-4 w-4 text-gray-500 mr-1" />
                        {client.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.company}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-500 mr-1" />
                        {format(new Date(client.createdAt), 'dd/MM/yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.lastOrder ? format(new Date(client.lastOrder), 'dd/MM/yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{client.totalSpent} €</div>
                      <div className="text-sm text-gray-500">{client.totalOrders} commande(s)</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/admin/messages?client=${client.id}`}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Contacter"
                      >
                        <Mail className="h-5 w-5 inline" />
                      </Link>
                      <button
                        onClick={() => handleEditClient(client)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Modifier"
                      >
                        <Edit className="h-5 w-5 inline" />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <Trash2 className="h-5 w-5 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal for adding/editing client */}
      {isModalOpen && currentClient && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmitClient}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {currentClient.id ? 'Modifier le client' : 'Ajouter un client'}
                  </h3>
                  
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={currentClient.name}
                      onChange={(e) => setCurrentClient({ ...currentClient, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={currentClient.email}
                      onChange={(e) => setCurrentClient({ ...currentClient, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="text"
                      id="phone"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={currentClient.phone}
                      onChange={(e) => setCurrentClient({ ...currentClient, phone: e.target.value })}
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      id="company"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={currentClient.company}
                      onChange={(e) => setCurrentClient({ ...currentClient, company: e.target.value })}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    * Champs obligatoires
                  </p>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {currentClient.id ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;